import React from 'react';
import { Input } from 'antd';
import {
  Banner,
  ComboRadio,
  Gap,
  Gutter,
  Paragraph,
  Text,
} from '@common/components';
import { WorkforceGroupFormProps } from './workforce-group-form';
import type { WorkforceGroupUtilizationRule } from '@common/graphql';

type Props = {
  onUpdateInputData: WorkforceGroupFormProps['onUpdateInputData'];
  weeklyUtilization?: [number, number];
  utilizationRule?: WorkforceGroupUtilizationRule;
};

export const WorkforceGroupFormUtilizationRulesTab = ({
  onUpdateInputData,
  weeklyUtilization,
  utilizationRule,
}: Props) => (
  <Gutter direction="vertical" size="md">
    <ComboRadio
      value={utilizationRule}
      onChange={(e) => onUpdateInputData('utilizationRule', e.target.value)}
      options={[
        {
          label: 'No rules enforced',
          value: 'UNRESTRICTED',
        },
        {
          label: 'Fixed Week(s) (Saturday – Sunday)',
          value: 'WEEKLY',
          content: (
            <WeeklyOptionContent
              onUpdateInputData={onUpdateInputData}
              weeklyUtilization={weeklyUtilization}
            />
          ),
        },
        {
          label: 'Rolling 7-day',
          value: 'ROLLING_WINDOW',
          content: (
            <RollingWindowOptionContent
              onUpdateInputData={onUpdateInputData}
              weeklyUtilization={weeklyUtilization}
            />
          ),
        },
      ]}
    />
  </Gutter>
);

type WeeklyOptionContentProps = {
  onUpdateInputData: WorkforceGroupFormProps['onUpdateInputData'];
  weeklyUtilization?: [number, number];
};

const WeeklyOptionContent = ({
  onUpdateInputData,
  weeklyUtilization,
}: WeeklyOptionContentProps) => (
  <Gap vertical size="md">
    <DeferToCoreSchedulingSystemWarning />
    <div>
      <Text weight="bold">Set minimum work hours</Text>
      <Paragraph>
        Define a baseline number of hours a staff member must be scheduled to
        work before being eligible to pick up shifts in Works.
      </Paragraph>
      <Gutter direction="top">
        <label>
          <Gap vertical size="xs">
            <Text>Min. (hours)</Text>
            <Input
              type="number"
              min={0}
              max={168}
              value={weeklyUtilization?.[0]}
              onChange={(event) =>
                onUpdateInputData('weeklyUtilization', [
                  Number(event.target.value) || 0,
                  weeklyUtilization?.[1] || 168,
                ])
              }
              size="large"
              style={{ maxWidth: 115 }}
              placeholder="0"
            />
          </Gap>
        </label>
      </Gutter>
    </div>
    <div>
      <Text bold>Set maximum work hours</Text>
      <Paragraph>
        Define a cap on the total hours a staff member can work in the period
        below.
      </Paragraph>
      <Gutter direction="top">
        <label>
          <Gap vertical size="xs">
            <Text>Max. (hours)</Text>
            <Input
              type="number"
              min={0}
              max={168}
              value={weeklyUtilization?.[1]}
              onChange={(event) =>
                onUpdateInputData('weeklyUtilization', [
                  weeklyUtilization?.[0] || 0,
                  Number(event.target.value) || 168,
                ])
              }
              size="large"
              style={{ maxWidth: 115 }}
              placeholder="168"
            />
          </Gap>
        </label>
      </Gutter>
    </div>
  </Gap>
);

type RollingWindowOptionContentProps = {
  onUpdateInputData: WorkforceGroupFormProps['onUpdateInputData'];
  weeklyUtilization?: [number, number];
};

const RollingWindowOptionContent = ({
  onUpdateInputData,
  weeklyUtilization,
}: RollingWindowOptionContentProps) => (
  <Gap vertical fullWidth size="md">
    <DeferToCoreSchedulingSystemWarning />
    <Gap vertical fullWidth>
      <div>
        <Text weight="bold">Set the rolling 7-Day work limit</Text>
        <Paragraph>
          Define the minimum and maximum hours a staff member can work in any
          continuous 7-day window.
        </Paragraph>
      </div>
      <label style={{ width: '100%' }}>
        <Gap vertical fullWidth size="xs">
          <Text>Min. (hours)</Text>
          <Input
            type="number"
            min={0}
            max={168}
            value={weeklyUtilization?.[0]}
            onChange={(event) =>
              onUpdateInputData('weeklyUtilization', [
                Number(event.target.value) || 0,
                weeklyUtilization?.[1] || 168,
              ])
            }
            size="large"
            style={{ maxWidth: 115 }}
            placeholder="0"
          />
        </Gap>
      </label>
      <label style={{ width: '100%' }}>
        <Gap vertical fullWidth size="xs">
          <Text>Max. (hours)</Text>
          <Input
            type="number"
            min={0}
            max={168}
            value={weeklyUtilization?.[1]}
            onChange={(event) =>
              onUpdateInputData('weeklyUtilization', [
                weeklyUtilization?.[0] || 0,
                Number(event.target.value) || 168,
              ])
            }
            size="large"
            style={{ maxWidth: 115 }}
            placeholder="168"
          />
        </Gap>
      </label>
    </Gap>
  </Gap>
);

const DeferToCoreSchedulingSystemWarning = () => (
  <Banner type="warning" closable>
    <Paragraph>
      If your core scheduling system implements these rules, you can choose to
      defer to the core schedule policies. In that case, shifts may be cancelled
      or rejected at the time of booking in the core system.
    </Paragraph>
  </Banner>
);
